import type { ReactElement } from 'react';
import SafeNavLink from './SafeNavLink';
import { useAuth } from '../AuthContext';
import { STUDENT_HOME, TEACHER_HOME } from '../utils/roleHome';
import styles from './MobileTabBar.module.css';

interface Tab {
  to: string;
  label: string;
  end?: boolean;
  icon: 'courses' | 'times' | 'help' | 'profile';
}

/** Tabs pro Rolle (UX-Plan 5.1). Reihenfolge = Wichtigkeit, wie im Hauptmenü. */
export function tabsForRole(role: string | null): Tab[] {
  if (role === 'teacher') {
    return [
      // Kein Tab „Planung“ (wie im Hauptmenü): die Planung startet aus der Kurskarte.
      { to: TEACHER_HOME, label: 'Kurse', icon: 'courses' },
      { to: '/hilfe', label: 'Hilfe', icon: 'help' },
      { to: '/profile', label: 'Profil', icon: 'profile' },
    ];
  }
  if (role === 'student') {
    return [
      { to: '/student', label: 'Zeiten', end: true, icon: 'times' },
      { to: STUDENT_HOME, label: 'Kurse', icon: 'courses' },
      { to: '/hilfe', label: 'Hilfe', icon: 'help' },
      { to: '/profile', label: 'Profil', icon: 'profile' },
    ];
  }
  return [];
}

const ICONS: Record<Tab['icon'], ReactElement> = {
  courses: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v3H4zM4 10.5h16v3H4zM4 16h16v3H4z" /></svg>
  ),
  times: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16zm-1 3v6l4.5 2.7 1-1.7-3.5-2.1V7z" /></svg>
  ),
  help: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16zm-1 12h2v2h-2zm1-9a3.5 3.5 0 0 0-3.5 3.5h2a1.5 1.5 0 1 1 3 0c0 1.5-2.5 1.75-2.5 4h2c0-1.5 2.5-2 2.5-4A3.5 3.5 0 0 0 12 7z" /></svg>
  ),
  profile: (
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm0 10c4.4 0 8 2.2 8 5v3H4v-3c0-2.8 3.6-5 8-5zm0 2c-3.5 0-6 1.6-6 3v1h12v-1c0-1.4-2.5-3-6-3z" /></svg>
  ),
};

/**
 * Feste Tab-Leiste am unteren Rand für eingeloggte Lehrende und Studierende auf
 * kleinen Bildschirmen (UX-Plan 5.1). Ersetzt dort das Burger-Menü; die Links laufen
 * über SafeNavLink, damit der Unsaved-Changes-Guard greift. RootLayout setzt
 * `body[data-tabbar]`, worüber SaveBar, Footer und Toasts ihren Abstand beziehen.
 */
export default function MobileTabBar() {
  const { userRole } = useAuth();
  const tabs = tabsForRole(userRole);
  if (tabs.length === 0) return null;
  return (
    <nav className={styles.bar} aria-label="Hauptnavigation" data-testid="mobile-tab-bar">
      {tabs.map(tab => (
        <SafeNavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) => `${styles.tab} ${isActive ? styles.active : ''}`}
        >
          <span className={styles.icon}>{ICONS[tab.icon]}</span>
          <span className={styles.label}>{tab.label}</span>
        </SafeNavLink>
      ))}
    </nav>
  );
}
